/**
 * Shared display helpers for tour ground transport rows (pickup time, titles).
 */

function parsePickupTime(pickup: unknown): Date | null {
  if (pickup == null || typeof pickup !== 'object') return null
  const raw = (pickup as { time?: unknown }).time
  if (raw == null || raw === '') return null
  const d = raw instanceof Date ? raw : new Date(raw as string | number)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Location string from nested pickup `{ location }`, or empty. */
export function groundPickupLocationLabel(pickup: unknown): string {
  if (pickup == null) return ''
  if (typeof pickup === 'string') return pickup.trim()
  if (typeof pickup === 'object' && pickup !== null && 'location' in pickup) {
    const loc = (pickup as { location?: unknown }).location
    return typeof loc === 'string' ? loc.trim() : ''
  }
  return ''
}

/** Full date+time for subtitle, e.g. "4/23/26, 10:00 AM". */
export function groundPickupDateTimeLabel(pickup: unknown): string {
  const d = parsePickupTime(pickup)
  if (!d) return ''
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
}

/** Time only for headings, e.g. "10:00 AM". */
export function groundPickupTimeOnlyLabel(pickup: unknown): string {
  const d = parsePickupTime(pickup)
  if (!d) return ''
  return d.toLocaleTimeString(undefined, { timeStyle: 'short' })
}

function typeString(g: {
  transportType?: unknown
  transport_type?: unknown
  type?: unknown
}): string {
  const t = g.transportType ?? g.transport_type ?? g.type
  if (t == null) return ''
  const s = String(t).trim()
  return s
}

/**
 * Primary card title: "Leaving at 10:00 AM" when pickup time exists; otherwise transport type or "Transport n".
 * Optional muted suffix is the raw type when the main line is time-based (so "other" still appears, not as the headline).
 */
export function groundTransportCardHeading(
  g: { transportType?: unknown; transport_type?: unknown; type?: unknown; pickup?: unknown },
  index: number,
): { heading: string; typeSuffix: string | null; usedPickupTimeInHeading: boolean } {
  const timeOnly = groundPickupTimeOnlyLabel(g.pickup)
  const typ = typeString(g)
  if (timeOnly) {
    return {
      heading: `Leaving at ${timeOnly}`,
      typeSuffix: typ || null,
      usedPickupTimeInHeading: true,
    }
  }
  if (typ) {
    return { heading: typ, typeSuffix: null, usedPickupTimeInHeading: false }
  }
  return { heading: `Transport ${index + 1}`, typeSuffix: null, usedPickupTimeInHeading: false }
}

/**
 * Pickup subline segments (location and/or full datetime). When the heading already shows the clock time,
 * omit the redundant full datetime if we still have a location; if there is no location, keep full datetime.
 */
export function groundTransportPickupSublineParts(
  pickup: unknown,
  headingUsedPickupTime: boolean,
): string[] {
  const loc = groundPickupLocationLabel(pickup)
  const fullWhen = groundPickupDateTimeLabel(pickup)
  if (!headingUsedPickupTime) {
    return [loc, fullWhen].filter(Boolean)
  }
  if (loc) return [loc]
  if (fullWhen) return [fullWhen]
  return []
}
